<!--
 * A Vue component to load an image.
 *
 *
 * Properties
 * ==========
 * - name -
 * (Optional) The text that is used as id and name for all elements.
 *
 * - maxFileSize -
 * (Optional) A file size to validate against.
 *   If the image to load is bigger than provide maxFileSize, an error is shown.
 *
 * - value -
 * (Optional) To set the initial value of the input element.
 *
 *
 * Events
 * ==========
 * - input -
 * This event is sent when user changes the input value.
 * The object to sent along with this event is a base64 image file.
 *
 * - loadFileValidState -
 * The validation state of loading file.
 *
 * Usage
 * ==========
 * -It is not mandatory to listen to the emitted input event from this component.
 * Jus use the v-model in your parent component to pass the intial value to this component
 * and receive the new loaded image from this component automatically.
 *
 *-You can put the standard html input attributes, sucht as required,
 * autofocus, etc. directly from the parent component without explicit binding.
-->
<template>
    <div>
        <img
            :id="`imageLoaderImg${name}`"
            :src="imageData"
            :class="imageData && imageData.length > 0 ? imgExtendedClass : ''"
            alt=""
        />
        <base-button
            v-if="imageData && imageData.length > 0"
            :id="`imageLoaderDeleteBtn${name}`"
            variant="link"
            size="sm"
            @click="$_removeImage"
            :text="$t('btnDeleteLabel')"
            class="mb-1"
        >
        </base-button>
        <input
            v-bind="$attrs"
            type="file"
            ref="imageloaderinput"
            :id="`imageLoaderInput${name}`"
            :name="`imageLoaderInput${name}`"
            accept="image/*"
            @change="$_onChange"
        />
    </div>
</template>

<script>
    import BaseButton from '../components/BaseComponents/base-button.vue';
    import modalMixin from '../components/Mixins/ModalMixin.vue';
    import { stringNotEmpty } from '../services/utilities';

    export default {
        inheritAttrs: false,
        name: 'ImageLoader',

        mixins: [modalMixin],

        components: { 'base-button': BaseButton },

        props: {
            value: { type: String, default: '' },
            name: { type: String, default: '' },
            // default max size 1MB
            maxFileSize: { type: Number, default: 1024 * 1024 },
            maxFileSizeErrorMessage: { type: String, default: '1MB' },
            imgExtendedClass: { type: String, default: '' }
        },

        data() {
            return {
                imageFile: '',
                fileReader: null,
                imageTooLarge: false,
                imageMetadata: {
                    fileName: '',
                    fileType: ''
                }
            };
        },

        computed: {
            imageData: {
                get() {
                    return this.imageFile || this.value;
                },
                set(input) {
                    this.imageFile = input;
                    this.$emit('input', input);
                    this.$emit('metadata', this.imageMetadata);
                    return input;
                }
            }
        },

        methods: {
            /*
             * Creates a filereader object and subscribe to onload event.
             */
            $_initFileReader() {
                this.fileReader = new FileReader();
                this.fileReader.onload = (e) => {
                    this.imageData = e.target.result;
                };
            },

            /*
             * Starts the process of loading image
             * @param {event} input event which contain the image data
             */
            $_onChange(event) {
                const input = event.target;
                if (input.value) {
                    if (this.$_validateFile(input)) {
                        this.$_initFileReader();
                        this.imageMetadata.fileName = input.files[0].name;
                        this.imageMetadata.fileType = input.files[0].type;
                        this.fileReader.readAsDataURL(input.files[0]);
                    }
                } else {
                    this.$_removeImage();
                }
            },

            /*
             * Removes the loaded image
             */
            $_removeImage() {
                this.imageMetadata.fileName = '';
                this.imageMetadata.fileType = '';
                this.imageData = '';
                // Reset the value so that the same file can be selected again
                this.$refs.imageloaderinput.value = '';
            },

            /*
             * Validate image on size
             * @param {Object} image - The image to be validated, properties to validate on: image.filename, image.filetype & image.filesize
             * @returns {bool} returns boolean value indicating if the image is valid.
             */
            $_validateFile(input) {
                let valid = true;
                let loadImageErrorMessage = '';
                if (!input.files) {
                    valid = false;
                    loadImageErrorMessage = this.$t('loadFileErrNoFileIsChosen');
                } else if (!input.files[0]) {
                    valid = false;
                    loadImageErrorMessage = this.$t('loadFileErrFileNoContent');
                } else if (input.files[0].size > this.maxFileSize) {
                    valid = false;
                    loadImageErrorMessage = this.$t('loadFileErrFileTooLarge', [
                        `${this.maxFileSizeErrorMessage}`
                    ]);
                    this.imageTooLarge = true;
                } else {
                    this.imageTooLarge = false;
                }
                if (stringNotEmpty(loadImageErrorMessage)) {
                    this.$_removeImage();
                    this.showToast({
                        message: loadImageErrorMessage,
                        variant: 'danger',
                        autoHideDelay: 5000,
                        noCloseButton: true
                    });
                }
                // Send validation state to the subscribers
                this.$emit('loadFileValidState', valid);
                return valid;
            }
        }
    };
</script>
